// src/pages/ManageSeats.js
//
// What a buyer gets after checkout. Before this page existed, a school could
// buy 30 seats, the webhook would mint 30 seat documents, and there was no
// way in the entire app to put anyone in them.

import React, { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db, functions } from "../firebase/firebaseConfig";

const seatLabel = { teacher: "Educator", student: "Student", single: "Account" };

export default function ManageSeats() {
  const navigate = useNavigate();
  const [uid, setUid] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [seatsByPurchase, setSeatsByPurchase] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteFor, setInviteFor] = useState(null); // seatId being invited
  const [emailDraft, setEmailDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      setUid(user.uid);
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!uid) return undefined;
    const q = query(
      collection(db, "licensePurchases"),
      where("buyerUid", "==", uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPurchases(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Purchases listener error:", err);
        setError("Couldn't load your licenses.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  // One seats listener per purchase. Most buyers have exactly one.
  useEffect(() => {
    const unsubs = purchases.map((p) =>
      onSnapshot(
        collection(db, "licensePurchases", p.id, "seats"),
        (snap) => {
          setSeatsByPurchase((prev) => ({
            ...prev,
            [p.id]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          }));
        },
        (err) => console.error("Seats listener error:", err)
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [purchases]);

  const totals = useMemo(() => {
    let total = 0;
    let filled = 0;
    Object.values(seatsByPurchase).forEach((seats) => {
      seats.forEach((s) => {
        total += 1;
        if (s.claimed || s.assignedUid) filled += 1;
      });
    });
    return { total, filled, open: total - filled };
  }, [seatsByPurchase]);

  const sendInvite = async (purchaseId, seatId) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const inviteToSeat = httpsCallable(functions, "inviteToSeat");
      const { data } = await inviteToSeat({
        purchaseId,
        seatId,
        email: emailDraft,
      });
      setNotice(
        data.emailSent
          ? `Invitation sent to ${emailDraft}.`
          : `Invitation created, but the email didn't go out. Send this link directly: ${data.claimUrl}`
      );
      setInviteFor(null);
      setEmailDraft("");
    } catch (err) {
      console.error("Invite error:", err);
      setError(err?.message || "Couldn't send that invitation.");
    }
    setBusy(false);
  };

  const revoke = async (purchaseId, seatId, who) => {
    const ok = window.confirm(
      `Free up this seat?${who ? ` ${who} will lose access immediately.` : ""}`
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const revokeSeat = httpsCallable(functions, "revokeSeat");
      await revokeSeat({ purchaseId, seatId });
      setNotice("Seat freed up. You can invite someone else to it now.");
    } catch (err) {
      console.error("Revoke error:", err);
      setError(err?.message || "Couldn't free that seat.");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.card}><p>Loading your seats…</p></div>
      </div>
    );
  }

  if (!purchases.length) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h1 style={s.title}>No seats yet</h1>
          <p style={s.body}>
            Once you purchase a plan with seats, they'll show up here for you
            to assign.
          </p>
          <button
            type="button"
            style={s.primary}
            onClick={() => navigate("/choose-plan")}
          >
            View plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ ...s.card, maxWidth: 820 }}>
        <h1 style={s.title}>Manage seats</h1>
        <p style={s.sub}>
          {totals.filled} of {totals.total} filled · {totals.open} open
        </p>

        {error && <div style={s.error}>{error}</div>}
        {notice && <div style={s.notice}>{notice}</div>}

        {purchases.map((p) => {
          const seats = seatsByPurchase[p.id] || [];
          return (
            <div key={p.id} style={s.purchase}>
              <div style={s.purchaseHead}>
                <div>
                  <strong>{seatLabel[p.seatType] || "Seat"} license</strong>
                  <div style={s.meta}>
                    {p.state ? `${p.state} · ` : ""}
                    {p.grade ? `Grade ${p.grade} · ` : ""}
                    {p.seatsPurchased} seat{p.seatsPurchased === 1 ? "" : "s"}
                  </div>
                </div>
                <span
                  style={{
                    ...s.badge,
                    background: p.status === "active" ? "#dcfce7" : "#fee2e2",
                    color: p.status === "active" ? "#166534" : "#991b1b",
                  }}
                >
                  {p.status}
                </span>
              </div>

              {p.status !== "active" && (
                <p style={s.warn}>
                  This license isn't active, so its seats can't be assigned
                  until billing is sorted out.
                </p>
              )}

              <ul style={s.seatList}>
                {seats.map((seat, i) => {
                  const taken = seat.claimed || seat.assignedUid;
                  const invited = !taken && seat.invitedEmail;
                  return (
                    <li key={seat.id} style={s.seat}>
                      <span style={s.seatNum}>{i + 1}</span>

                      <span style={s.seatWho}>
                        {taken ? (
                          <>
                            <strong>{seat.assignedEmail || "Claimed"}</strong>
                            <span style={s.seatState}>active</span>
                          </>
                        ) : invited ? (
                          <>
                            <strong>{seat.invitedEmail}</strong>
                            <span style={s.seatState}>invite pending</span>
                          </>
                        ) : (
                          <span style={s.seatState}>open</span>
                        )}
                      </span>

                      <span style={s.seatActions}>
                        {taken || invited ? (
                          <button
                            type="button"
                            style={s.smallGhost}
                            disabled={busy}
                            onClick={() =>
                              revoke(p.id, seat.id, seat.assignedEmail || seat.invitedEmail)
                            }
                          >
                            {taken ? "Remove" : "Cancel invite"}
                          </button>
                        ) : inviteFor === seat.id ? (
                          <span style={s.inviteRow}>
                            <input
                              type="email"
                              value={emailDraft}
                              onChange={(e) => setEmailDraft(e.target.value)}
                              placeholder={
                                p.seatType === "student"
                                  ? "Parent or guardian email"
                                  : "Email address"
                              }
                              style={s.inviteInput}
                            />
                            <button
                              type="button"
                              style={s.small}
                              disabled={busy || !emailDraft}
                              onClick={() => sendInvite(p.id, seat.id)}
                            >
                              Send
                            </button>
                            <button
                              type="button"
                              style={s.smallGhost}
                              onClick={() => {
                                setInviteFor(null);
                                setEmailDraft("");
                              }}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            style={s.small}
                            disabled={busy || p.status !== "active"}
                            onClick={() => {
                              setInviteFor(seat.id);
                              setEmailDraft("");
                            }}
                          >
                            Invite
                          </button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              {p.seatType === "student" && (
                <p style={s.minorNote}>
                  Student seats are invited through a parent, guardian, or
                  educator's email address. That adult creates and manages the
                  account — invitations aren't sent to children directly.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#f1f5f9", padding: 20 },
  card: {
    background: "#fff",
    maxWidth: 560,
    width: "100%",
    margin: "0 auto",
    borderRadius: 12,
    padding: 28,
    boxShadow: "0 20px 40px rgba(0,0,0,0.10)",
  },
  title: { margin: 0, fontSize: "clamp(1.6rem, 4vw, 2rem)", color: "#111" },
  sub: { color: "#555", fontSize: 14, marginTop: 6, marginBottom: 20 },
  body: { color: "#444", fontSize: 15, lineHeight: 1.6 },
  purchase: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 18,
    marginBottom: 18,
  },
  purchaseHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  meta: { fontSize: 13, color: "#6b7280", marginTop: 4 },
  badge: {
    fontSize: 12,
    fontWeight: 700,
    padding: "3px 10px",
    borderRadius: 999,
    textTransform: "uppercase",
  },
  warn: {
    fontSize: 13,
    color: "#92400e",
    background: "#fef3c7",
    padding: "8px 10px",
    borderRadius: 8,
    marginTop: 12,
  },
  seatList: { listStyle: "none", padding: 0, margin: "14px 0 0" },
  seat: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap",
  },
  seatNum: {
    width: 26,
    height: 26,
    lineHeight: "26px",
    textAlign: "center",
    borderRadius: "50%",
    background: "#f1f5f9",
    fontSize: 12,
    color: "#475569",
    flexShrink: 0,
  },
  seatWho: {
    flex: "1 1 160px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    fontSize: 14,
    overflowWrap: "anywhere",
  },
  seatState: { fontSize: 12, color: "#6b7280" },
  seatActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  inviteRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
  inviteInput: {
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    minWidth: 190,
  },
  small: {
    padding: "7px 14px",
    borderRadius: 6,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  smallGhost: {
    padding: "7px 14px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  primary: {
    marginTop: 18,
    padding: "11px 18px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  minorNote: { fontSize: 12, color: "#6b7280", marginTop: 12, lineHeight: 1.5 },
  error: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 14,
  },
  notice: {
    background: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 14,
    overflowWrap: "anywhere",
  },
};
